/**
 * Non-cryptographic fallback ID using Math.random().
 * ⚠️ WARNING: This uses Math.random() which is NOT cryptographically secure.
 * Only used when the Web Crypto API is unavailable (rare scenarios).
 * Suitable ONLY for UI-only identifiers (notification IDs, error IDs, etc.).
 * NEVER use for security-sensitive operations like tokens, session IDs, or secrets.
 */
function generateFallbackId(): string {
  // Note: Math.random() is predictable and should not be used for security purposes.
  // This fallback is only for UI identifiers when crypto.getRandomValues is unavailable.
  const r1 = Math.floor(Math.random() * 0xffffffff).toString(36);
  const r2 = Math.floor(Math.random() * 0xffffffff).toString(36);
  return r1 + r2;
}

/**
 * Generates a random ID for use as a UI identifier.
 * Prefers the Web Crypto API for randomness; falls back to Math.random()
 * when crypto is unavailable (e.g. restricted environments, SSR, older browsers).
 * Do NOT use for security-sensitive values such as tokens or secrets.
 */
export function generateSecureId(prefix?: string): string {
  let randomPart: string;

  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.getRandomValues) {
    const array = new Uint32Array(2);
    cryptoApi.getRandomValues(array);
    // Type assertion safe: Uint32Array of length 2 guarantees these indices exist
    randomPart = array[0]!.toString(36) + array[1]!.toString(36);
  } else {
    randomPart = generateFallbackId();
  }

  const timestamp = Date.now().toString(36);
  return prefix ? `${prefix}_${timestamp}_${randomPart}` : `${timestamp}_${randomPart}`;
}

/**
 * Generates a notification ID for UI purposes.
 * Uses Web Crypto when available; falls back to Math.random() if unavailable.
 * Not suitable for security-sensitive operations.
 */
export function generateNotificationId(): string {
  return generateSecureId('notification');
}

/**
 * Generates an error ID for UI purposes.
 * Uses Web Crypto when available; falls back to Math.random() if unavailable.
 * Not suitable for security-sensitive operations.
 */
export function generateErrorId(): string {
  return generateSecureId('err');
}
